import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useRevealOnce } from '@/hooks/useRevealOnce';
import { useGradientBackground } from '@/utils/gradientBackground';

interface PackageFeature {
  text: string;
  group?: string;
}

interface Package {
  title: string;
  summary: string;
  price: string;
  oneLiner: string;
  bestFor: string[];
  features: PackageFeature[];
  cta: string;
}

export const packages: Package[] = [
  {
    title: 'Starter',
    summary: 'Brand',
    price: '$2,000',
    oneLiner: 'Wrap your domain in a clear, professional brand you can use everywhere.',
    bestFor: [
      'You have a domain and an idea, but no cohesive visual identity.',
      "You're not ready for a full site yet, but want to stop looking DIY.",
      'You want a flexible brand you can plug into a future site, proposals, and social.',
    ],
    features: [
      { text: '30–60 minute clarity call (audience, offers, positioning).' },
      { text: 'Logo/wordmark + simple icon tailored to your domain and niche.' },
      { text: 'Color palette + typography pairings.' },
      {
        text: '1–2 page Brand Starter PDF with logo usage, colors, type, and a simple brand one-liner.',
      },
    ],
    cta: 'Start',
  },
  {
    title: 'Basic',
    summary: 'Brand + Website',
    price: '$5,000',
    oneLiner: 'Your domain, brand, and a complete professional website ready to take real enquiries.',
    bestFor: [
      "You're ready for a \"this is my business\" website, not just a landing page.",
      'You want a site that explains what you do and nudges visitors to book a call.',
      "You don't want to wrestle with DNS, hosting, SSL, or forms.",
    ],
    features: [
      { group: 'Brand', text: 'Everything in **Starter**' },
      { group: 'Website', text: '4–6 page site (Home, Services, About, Contact, plus optional FAQ/Case Study).' },
      { group: 'Website', text: 'Conversion-focused copy written from a structured intake and interview.' },
      {
        group: 'Website',
        text: 'Mobile-responsive design, basic on-page SEO (titles, meta descriptions, headings).',
      },
      { group: 'Setup', text: 'Domain, hosting, SSL configured.' },
      { group: 'Setup', text: 'Contact or "Book a call" form set up and tested.' },
      { group: 'Setup', text: 'Basic analytics installed.' },
    ],
    cta: 'Launch',
  },
  {
    title: 'Pro',
    summary: 'Brand + Website + AI runs it',
    price: '$10,000',
    oneLiner:
      'Everything in Basic, plus the AI workspace and automations I build at Managz.com, so the business behind your domain starts running itself instead of running you.',
    bestFor: [
      "You're serious about building or relaunching a service business on this domain.",
      'You want help with offers, pricing, and positioning — not just visuals.',
      'You want the routine work handled by AI, and you want to watch it working before you trust it with anything.',
    ],
    features: [
      { group: 'Strategy', text: '60–90 minute deep-dive strategy session (offers, pricing, ideal clients).' },
      { group: 'Strategy', text: 'Positioning statement, core messaging, and a simple brand story.' },
      { group: 'Brand & Website', text: 'Everything in **Basic**.' },
      {
        group: 'Brand & Website',
        text: 'Extra sections for process, case studies, or resources if needed.',
      },
      {
        group: 'AI Workspace & Automations',
        text: 'The same build I deliver at <a href="https://managz.com" class="font-semibold text-[#0000FF] underline underline-offset-2 hover:opacity-80">Managz.com</a>, included here instead of bought separately.',
      },
      {
        group: 'AI Workspace & Automations',
        text: 'A mapping session, then a written scope and a fixed price, before anything is built.',
      },
      {
        group: 'AI Workspace & Automations',
        text: '**One automation** built into the tools you already use. **Two $13,000. Three $15,000.**',
      },
      {
        group: 'AI Workspace & Automations',
        text: 'A visual workspace with long-term memory, so the AI understands your business and you can talk to it in plain English.',
      },
      {
        group: 'AI Workspace & Automations',
        text: 'A human approval step on anything client-facing, and a run-alongside period before it replaces how you work now.',
      },
      {
        group: 'AI Workspace & Automations',
        text: 'Documentation, a walkthrough, two rounds of changes and 30 days to fix anything off-scope. Everything in accounts you own, no monthly fee.',
      },
      { group: 'Launch', text: 'CRM, pipeline and notifications set up so enquiries never get lost.' },
      {
        group: 'Launch',
        text: '"First Client Playbook" plus a 30–60 day launch plan with weekly steps.',
      },
      {
        group: 'Launch',
        text: '2–3 follow-up strategy calls in the first 60–90 days.',
      },
    ],
    cta: 'Apply',
  },
];

export default function ServicesPackagesSection() {
  const sectionReveal = useRevealOnce({ amount: 0.2 });
  const backgroundStyle = useGradientBackground();

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' as const },
    },
  };

  const groupFeatures = (features: PackageFeature[]) => {
    const grouped: Record<string, string[]> = {};
    features.forEach((feature) => {
      const group = feature.group || 'General';
      if (!grouped[group]) {
        grouped[group] = [];
      }
      grouped[group].push(feature.text);
    });
    return grouped;
  };

  return (
    <>
      {/* Gradient Divider */}
      <div className="w-full h-8 bg-gradient-to-b from-black via-neutral-200 to-white" />

          {/* Main Section */}
          <section id="packages" className="relative w-full py-16 md:py-24" style={backgroundStyle}>
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <motion.div
              ref={sectionReveal.ref}
              variants={containerVariants}
              initial="hidden"
              animate={sectionReveal.controls}
            >
              {/* Heading Section */}
              <div className="text-center mb-12 md:mb-16">
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground mb-6">
                  Choose your launch blueprint
                </h2>
                <p className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-3xl mx-auto">
                  Every package is designed to move you from "great domain, weak system" to a clearer, more credible,
                  more client-ready business. Pick the level that matches where you are right now.
                </p>
              </div>

              {/* Pricing Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 items-start">
                {packages.map((pkg, index) => {
                  const groupedFeatures = groupFeatures(pkg.features);
                  const hasGroups = Object.keys(groupedFeatures).length > 1 || pkg.features[0]?.group;

                  return (
                    <motion.div key={index} variants={cardVariants}>
                      <Card className="flex flex-col border-slate-200 transition-all duration-200 hover:shadow-lg">
                        <CardHeader className="pb-4 text-center">
                          <CardTitle className="text-5xl md:text-6xl font-bold tracking-tight mb-3">
                            {pkg.title}
                          </CardTitle>
                          <div className="space-y-2">
                            <div className="text-3xl md:text-4xl font-semibold text-foreground tracking-tight">
                              <span className="text-lg font-medium text-muted-foreground">From </span>
                              {pkg.price}
                            </div>
                            <div className="text-xl md:text-2xl font-semibold text-[#0000FF] leading-snug">
                              {pkg.summary}
                            </div>
                          </div>
                        </CardHeader>

                        <CardContent className="flex-1 flex flex-col space-y-6">
                          <p className="text-[17px] text-slate-800 leading-relaxed">{pkg.oneLiner}</p>

                          {/* Best for you if */}
                          <div>
                            <h4 className="text-base font-semibold text-foreground mb-2">Best for you if</h4>
                            <ul className="space-y-2">
                              {pkg.bestFor.map((item, i) => (
                                <li key={i} className="flex items-start gap-2 text-base text-slate-700">
                                  <span className="text-blue-600 mt-1.5 shrink-0">•</span>
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>

                          {/* What's included */}
                          <div>
                            <h4 className="text-base font-semibold text-foreground mb-3">What's included</h4>
                            <div className="space-y-4">
                              {hasGroups
                                ? Object.entries(groupedFeatures).map(([group, items]) => (
                                    <div key={group}>
                                      <div className="text-sm font-semibold tracking-wide text-slate-600 uppercase mb-2">
                                        {group}
                                      </div>
                                      <ul className="space-y-2">
                                        {items.map((item, i) => (
                                          <li key={i} className="flex items-start gap-2 text-base text-slate-700">
                                            <span className="text-blue-600 mt-1.5 shrink-0">•</span>
                                            <span
                                              dangerouslySetInnerHTML={{
                                                __html: item.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>'),
                                              }}
                                            />
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  ))
                                : pkg.features.map((feature, i) => (
                                    <li key={i} className="flex items-start gap-2 text-base text-slate-700">
                                      <span className="text-blue-600 mt-1.5 shrink-0">•</span>
                                      <span>{feature.text}</span>
                                    </li>
                                  ))}
                            </div>
                          </div>

                          {/* CTA Button */}
                          <div className="mt-auto pt-4">
                            <Button
                              asChild
                              className="w-full text-[17px] bg-[#0000FF] text-white hover:bg-[#0000EE] hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200 ease-out"
                              size="lg"
                            >
                              <Link to="/contact">{pkg.cta}</Link>
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
}

